/**
 * SearchingIndicator
 *
 * Loading skeleton shown while ANOUK looks up circuits (between the worker's
 * SEARCH_STARTED event and CIRCUITS_FOUND). It makes the on-screen change legible:
 * the moment the agent starts speaking ("je regarde ce qu'on a…"), a shimmering
 * placeholder card appears where the real circuit cards will land, so the search
 * never happens in silence with an unexplained screen change.
 *
 * Layout mirrors TripPreviewCard (glassmorphism, same footprint) for a smooth swap.
 */
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';

interface SearchingIndicatorProps {
  /** Optional message from the store, e.g. "Je cherche les meilleurs circuits pour le Népal…" */
  message?: string;
}

export function SearchingIndicator({ message }: SearchingIndicatorProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', damping: 20, stiffness: 120 }}
      role="status"
      aria-live="polite"
      className="group relative overflow-hidden bg-slate-900/30 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-sm md:w-96"
    >
      {/* Message row */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nomade-accent/20">
          <Search className="h-4 w-4 text-nomade-accent animate-pulse" />
        </span>
        <p className="text-sm font-medium text-white/90">
          {message || 'Je cherche les meilleurs circuits…'}
        </p>
      </div>

      {/* Shimmering skeleton lines */}
      <div className="space-y-2 px-4 pb-4">
        <div className="h-24 w-full animate-pulse rounded-lg bg-white/10" />
        <div className="h-3 w-3/4 animate-pulse rounded bg-white/10" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-white/10" />
      </div>
    </motion.div>
  );
}
